import { z } from 'zod';
import { IsoDateTimeSchema } from '../common/primitives';

/**
 * Microphone capture — the payloads for admin-triggered voice recording on a
 * company device.
 *
 * Everything here obeys the same rules the rest of the product already lives
 * by, and they are worth stating because audio is the most sensitive thing the
 * app touches:
 *
 *  • **Consented, and visible.** The device records only after the employee has
 *    agreed on-device AND granted RECORD_AUDIO — the same two-gate pattern as
 *    call logs — and Android shows its own microphone indicator the whole time.
 *    Nothing here is, or can be, covert; the OS forbids it.
 *  • **Never lost.** A segment is deleted from the device outbox only once the
 *    server acknowledges its `clientUuid`, exactly like a location point.
 *  • **One segment per request.** Audio is far larger than a point batch, so
 *    each ~60 s segment is uploaded on its own rather than batched, and carries
 *    a precomputed amplitude envelope so the panel can draw a waveform without
 *    ever decoding the audio.
 */

/** The container/codec of an uploaded segment. AAC-LC in an MP4/M4A container. */
export const AudioCodec = z.enum(['aac']);
export type AudioCodec = z.infer<typeof AudioCodec>;

/**
 * The amplitude envelope — one byte per sample, 0 (silence) to 255 (peak).
 *
 * This is what the panel draws as the waveform and reads as "voice spikes". It
 * is computed on the device from the recorder's own amplitude stream while the
 * segment records, so the server never decodes the audio to draw it and the
 * panel never downloads the audio just to show the shape.
 *
 * A minute at 10 Hz is 600 bytes — trivial next to the ~180 KB of audio it
 * summarises, and the reason scrubbing a day of recordings is instant.
 */
export const PeaksSchema = z.array(z.number().int().min(0).max(255)).max(36_000);

/** Amplitude samples per second in `peaks`. 10 Hz is smooth and tiny. */
export const PEAK_HZ = 10;

/**
 * The largest a single segment upload may be (base64 of the audio bytes).
 *
 * 60 s of mono AAC at 24 kbps is ~180 KB, ~240 KB base64. The cap sits well
 * above that so an occasional longer or higher-bitrate segment still lands,
 * and well under the API's 2 MB body limit.
 */
export const AUDIO_MAX_SEGMENT_BASE64 = 1_400_000;

export const AudioSegmentUploadSchema = z
  .object({
    /** Idempotency key. A replayed upload resolves to the same row. */
    clientUuid: z.string().uuid(),
    /** The tracking session the recording belongs to. */
    sessionUuid: z.string().uuid(),
    /** Capture window on the device clock. */
    startedAt: IsoDateTimeSchema,
    endedAt: IsoDateTimeSchema,
    durationMs: z.number().int().positive().max(10 * 60_000),
    codec: AudioCodec.default('aac'),
    sampleRate: z.number().int().positive().max(48_000),
    bitRate: z.number().int().positive().max(320_000),
    channels: z.number().int().min(1).max(2),
    peaks: PeaksSchema,
    peakHz: z.number().int().positive().max(100).default(PEAK_HZ),
    /**
     * Why the mic was on. Only ever `admin` today — the recording was triggered
     * by an administrator command — but recorded explicitly so a future
     * employee-initiated clip is never confused with a remote one.
     */
    trigger: z.enum(['admin']).default('admin'),
    /** The segment audio, base64-encoded. */
    audioBase64: z.string().max(AUDIO_MAX_SEGMENT_BASE64),
  })
  .strict();
export type AudioSegmentUpload = z.infer<typeof AudioSegmentUploadSchema>;

/** One-segment disposition, the audio analogue of the point-batch result. */
export const AudioSegmentResultSchema = z.object({
  clientUuid: z.string().uuid(),
  /** 'accepted' = stored now, 'duplicate' = already had it. Both mean "drop it". */
  status: z.enum(['accepted', 'duplicate']),
  serverTime: z.string(),
});
export type AudioSegmentResult = z.infer<typeof AudioSegmentResultSchema>;

// ---------------------------------------------------------------------------
// Admin-facing DTOs
// ---------------------------------------------------------------------------

export const AudioSegmentDtoSchema = z.object({
  id: z.number().int(),
  startedAt: z.string(),
  endedAt: z.string(),
  durationMs: z.number().int(),
  /** received_at − startedAt, seconds: how late this segment reached the server. */
  lagSeconds: z.number().int(),
  sizeBytes: z.number().int(),
  peakHz: z.number().int(),
  peaks: PeaksSchema,
  /** Where the panel fetches the audio bytes. Auth travels in the header. */
  streamUrl: z.string(),
});
export type AudioSegmentDto = z.infer<typeof AudioSegmentDtoSchema>;

/**
 * A run of segments the admin plays as one clip.
 *
 * The device records in ~60 s pieces so nothing is lost on a crash, but a
 * supervisor thinks in "the recording from 14:05 to 14:12", not twelve files.
 * Segments captured back-to-back are grouped into one recording; a real gap
 * (the mic was switched off and on again) starts a new one.
 */
export const AudioRecordingDtoSchema = z.object({
  /** The first segment's id — stable handle for this recording. */
  id: z.number().int(),
  startedAt: z.string(),
  endedAt: z.string(),
  /** Sum of segment durations — actual audio, excluding any gaps. */
  durationMs: z.number().int(),
  segmentCount: z.number().int(),
  totalBytes: z.number().int(),
  /**
   * The whole recording's envelope, downsampled to a fixed width so the panel
   * draws one waveform strip per recording without holding every sample. The
   * per-segment peaks stay on `segments` for the detailed view.
   */
  overviewPeaks: PeaksSchema,
  /** The loudest sample in the recording, so a flat/quiet clip reads as such. */
  peakLevel: z.number().int().min(0).max(255),
  segments: z.array(AudioSegmentDtoSchema),
});
export type AudioRecordingDto = z.infer<typeof AudioRecordingDtoSchema>;

export const AudioDayDtoSchema = z.object({
  employeeId: z.number().int(),
  date: z.string(),
  timezone: z.string(),
  recordings: z.array(AudioRecordingDtoSchema),
  summary: z.object({
    recordingCount: z.number().int(),
    segmentCount: z.number().int(),
    /** Total captured audio across the day. */
    totalSeconds: z.number().int(),
    totalBytes: z.number().int(),
  }),
});
export type AudioDayDto = z.infer<typeof AudioDayDtoSchema>;
